import {Box, P} from 'exsportia-components'
import React, {FC, useCallback, useState} from 'react'

import {
  useCreateMembershipCancelRequest,
  useMembershipCancelRequests,
  useWithdrawMembershipCancelRequest,
} from '../../../api/subscription-bookings/hooks'
import {MembershipCancelRequestStatusEnum} from '../../../api/subscription-bookings/types'
import {Button} from '../../../ui/components/button/button'
import {useLocale} from '../../../utils/i18n'
import {MembershipCancelRequestForm} from './membership-cancel-request-form'
import {MembershipCancelStatusCard} from './membership-cancel-status-card'

type Props = {
  subscriptionId?: string
  // Cancelled or expired memberships have nothing left to cancel.
  isMembershipEnded: boolean
}

export const MembershipCancelRequestSection: FC<Props> = ({subscriptionId, isMembershipEnded}) => {
  const {l} = useLocale()
  const {data: requests, isLoading} = useMembershipCancelRequests(subscriptionId)
  const {mutate: sendRequest, isLoading: isSending} = useCreateMembershipCancelRequest()
  const {mutate: withdrawRequest, isLoading: isWithdrawing} = useWithdrawMembershipCancelRequest()
  const [isFormOpen, setIsFormOpen] = useState(false)

  // Newest first: the latest one is the one the card shows. Only one can be open at a time.
  const latest = requests?.[0]
  const openRequest = requests?.find((request) => request.status === MembershipCancelRequestStatusEnum.REQUESTED)

  const handleSend = useCallback(
    (reason: string) => {
      if (!subscriptionId) {
        return
      }
      sendRequest({subscriptionId, reason}, {onSuccess: () => setIsFormOpen(false)})
    },
    [sendRequest, subscriptionId],
  )

  const handleWithdraw = useCallback(() => {
    if (!subscriptionId || !openRequest) {
      return
    }
    withdrawRequest({subscriptionId, requestId: openRequest.uid})
  }, [withdrawRequest, subscriptionId, openRequest])

  if (!subscriptionId) {
    return null
  }

  return (
    <Box data-testid='membership-cancel-section'>
      {latest && (
        <Box mb={'16px'}>
          <MembershipCancelStatusCard
            request={latest}
            isWithdrawing={isWithdrawing}
            onWithdraw={handleWithdraw}
          />
        </Box>
      )}
      {!isLoading &&
        !openRequest &&
        (isFormOpen ? (
          <MembershipCancelRequestForm
            isSending={isSending}
            onSubmit={handleSend}
            onClose={() => setIsFormOpen(false)}
          />
        ) : (
          <>
            <P
              fontSize={'body'}
              mb={'12px'}
            >
              {l('membershipCancel_intro')}
            </P>
            <Button
              type='secondary'
              onClick={() => setIsFormOpen(true)}
              disabled={isMembershipEnded}
            >
              {l('membershipCancel_requestButton')}
            </Button>
          </>
        ))}
    </Box>
  )
}
